import { css, cx } from '@repo/pandacss/styled-system/css';
import { LinkType } from '@repo/segment/enums';
import { type AnalyticsType } from '@repo/segment/types';
import { EntityPlacementCtaTypeEnum } from '@repo/supergraph/marketplace/enums';
import Image from 'next/image';
import { useMemo } from 'react';
import { CtaButton } from '../../CtaButton';
import { CtaLink } from '../../CtaLink';
import { ShortHorizontalLayout } from '../../StarRating/ShortHorizontalLayout';
import { stripFederallyInsuredAnnotation } from '../../utils/cta-utils';
import { type SummaryTableRowWithDrivers } from '../types';
import { DEFAULT_RATING_TOOLTIP } from '../utils';

interface ProductCellProps {
  beforeMainContent?: React.ReactNode;
  analyticsPayload: AnalyticsType;
  marketplaceEntity: SummaryTableRowWithDrivers['marketplaceEntity'];
  className?: string;
}

const ProductImage = ({ source, alt }: { source: string; alt: string }) => (
  <Image src={source} alt={alt} width={122} height={44} style={{ objectFit: 'contain' }} />
);

export const ProductCell = ({
  beforeMainContent,
  analyticsPayload,
  marketplaceEntity,
  className,
}: ProductCellProps) => {
  const {
    displayName,
    name,
    image,
    starRating,
    reviewLink,
    federallyInsuredAnnotation,
    starRatingTooltip,
    ctaOverride: precomputedCtaOverride,
    cta: originalCta,
  } = marketplaceEntity;

  const ctaOverride = useMemo(() => {
    return stripFederallyInsuredAnnotation(
      precomputedCtaOverride ?? originalCta,
      federallyInsuredAnnotation,
    );
  }, [precomputedCtaOverride, originalCta, federallyInsuredAnnotation]);

  return (
    <td
      className={cx(
        css({
          bgColor: 'neutral.lightest',
          paddingRight: '0',
          paddingBottom: '0',
          paddingLeft: '0',
          borderRightWidth: 'thin',
          borderRightStyle: 'solid',
          borderRightColor: 'neutral.lighter2',
          borderTopColor: 'neutral.lighter2',
          borderBottomColor: 'neutral.lighter2',
          borderLeftColor: 'neutral.lighter2',
          paddingTop: {
            base: 'var(--mobile-cell-padding-top)',
            md: 'var(--cell-padding-top)',
          },
        }),
        className,
      )}
    >
      {beforeMainContent}
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          paddingInlineStart: '2',
          paddingInlineEnd: '2',
          paddingBlockStart: '4',
          paddingBlockEnd: '4',
          columnGap: '2',
          rowGap: '2',
        })}
      >
        {image?.source &&
          (ctaOverride ? (
            <CtaLink
              cta={{ ...ctaOverride, type: EntityPlacementCtaTypeEnum.ProductImage }}
              monetizable={analyticsPayload.monetizable}
              analyticsPayload={analyticsPayload}
              linkType={LinkType.PRODUCT_IMAGE}
              className={css({
                lineHeight: '[1.43]',
              })}
            >
              <ProductImage
                source={image.source}
                alt={image.alt || displayName || 'Product logo'}
              />
            </CtaLink>
          ) : (
            <ProductImage source={image.source} alt={image.alt || displayName || 'Product logo'} />
          ))}
        <p
          className={css({
            textAlign: 'center',
            fontSize: 'sm',
            fontWeight: '[325]',
            marginTop: '0',
            marginRight: '0',
            marginBottom: '0',
            marginLeft: '0',
          })}
        >
          {displayName || name}
        </p>
        <ShortHorizontalLayout
          starRating={starRating}
          reviewLink={reviewLink}
          starRatingTooltip={starRatingTooltip ?? DEFAULT_RATING_TOOLTIP}
        />
        {ctaOverride && (
          <div
            className={css({
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              columnGap: '2',
              rowGap: '2',
              width: 'full',
            })}
          >
            <CtaButton
              cta={ctaOverride}
              analyticsPayload={analyticsPayload}
              buttonClassName={css({ height: '[45px]' })}
              className={css({
                columnGap: '0',
                rowGap: '0',
              })}
              subtextClassName={css({
                textAlign: 'center',
                marginTop: '0',
                marginRight: '0',
                marginBottom: '0',
                marginLeft: '0',
              })}
              subtextContainerClassName={css({
                lineHeight: 'md',
              })}
              hideSubtext
              fullWidth
            />
            <span
              className={css({
                textStyle: 'textExtraSmall',
                textAlign: 'center',
                color: 'neutral.dark',
              })}
            >
              {ctaOverride.subtext}
            </span>
          </div>
        )}
        {ctaOverride?.subtext && federallyInsuredAnnotation && (
          <div
            className={css({
              bgColor: 'yellow.lighter2',
              borderTopLeftRadius: 'sm',
              borderTopRightRadius: 'sm',
              borderBottomRightRadius: 'sm',
              borderBottomLeftRadius: 'sm',
              width: 'full',
              textAlign: 'center',
              paddingInlineStart: '4',
              paddingInlineEnd: '4',
            })}
          >
            <span
              className={css({
                textStyle: 'textExtraSmall',
                color: 'neutral.darker',
                fontWeight: 'medium',
              })}
            >
              {federallyInsuredAnnotation}
            </span>
          </div>
        )}
      </div>
    </td>
  );
};
